import React, { useState } from "react";
import { Icon } from "../icons";
import { LOCALE, fmtDate, fmtDateTime, fmtReminderWhen, todayStr } from "../lib/format";
import { isHourlyJob, money } from "../lib/hourly";
import { LD_RISK_NOTE_KEYS, RISK_NOTE_KEYS, shuttleOf } from "../lib/selectors";
import { HourlyPricing } from "./HourlyPricing";
import {
  AiCallBlock,
  DestCard,
  LiabilityCard,
  PaymentMethodField,
  ReadField,
  S2Field,
  S2FieldYN,
  SaveButton,
  SectionLabel,
  SegYN,
  YesNoBadge,
  ZohoBadgeVal,
  ZohoLabel,
  makeS2Setter,
  NoteTextarea,
} from "./fields";

const LD_RISKS = [
  "Scope change — sales required",
  "Service required vs quoted",
  "Logistical misalignment — move dates, restrictions",
  "Delivery window misalignment",
];
const LOCAL_RISKS = [
  "Scope change",
  "Additional service needed",
  "Sales/client misalignment",
  "Move date change",
];
const LogisticsBlock = ({ job, set }) => (
  <>
    <SectionLabel icon="truck">Logistics of the Move</SectionLabel>
    <div className="field-row">
      <ReadField label="Move date (Zoho)">{fmtDate(job.moveDate)}</ReadField>
      <div className="field">
        <label>
          <ZohoLabel text="Packing date (Zoho)" included={!!job.packingDate} />
        </label>
        <div className="val">{fmtDate(job.packingDate)}</div>
      </div>
    </div>
    <div className="field-row">
      <div className="field">
        <label>
          <ZohoLabel text="Start time (Zoho)" included={!!job.startTime} />
        </label>
        <div className="val">{job.startTime || "—"}</div>
      </div>
      <div className="field">
        <label>
          <ZohoLabel text="Unpacking date (Zoho)" included={!!job.unpackingDate} />
        </label>
        <div className="val">{fmtDate(job.unpackingDate)}</div>
      </div>
    </div>
    <div className="field-row">
      <div className="field">
        <label>
          <ZohoLabel text="Total cubic feet (Zoho)" included={!!job.totalCubicFeet} />
        </label>
        <div className="val">{job.totalCubicFeet || "—"}</div>
      </div>
      <div className="field">
        <label>
          <ZohoLabel text="Loading date (Zoho)" included={!!job.loadingDate} />
        </label>
        <div className="val">{fmtDate(job.loadingDate)}</div>
      </div>
    </div>
    <div className="field">
      <label>Disposal service</label>
      <div className="val">
        <ZohoBadgeVal included={job.disposalIncluded} />
      </div>
    </div>
    <S2Field job={job} fieldKey="logisticsNotes" label="Notes" onChange={set} />
  </>
);

/** True when Zoho says the job stores anything (full or partial storage job, long term, short term or at the destination). */
export const zohoStores = (storage) =>
  !!storage &&
  (!!storage.fullStorage || !!storage.partialStorage || !!storage.lts || !!storage.stst || !!storage.storeAtDestination);

/**
 * The Storage status shown: the MC's pick, else what Zoho says, so a job that
 * needs no storage reads "Not Required" from the start (Asana 1220099429071097).
 */
export const storageStatusOf = (job) =>
  job.storageStatus || (job.storage ? (zohoStores(job.storage) ? "needed" : "not-required") : "");

const ZohoMoney = ({ value }) => (value != null && value !== "" ? money(value) : "—");

const StorageBlock = ({ job, set, actions }) => {
  const storage = job.storage || null;
  const status = storageStatusOf(job);
  const fromZoho = !job.storageStatus && status !== "";
  const showStorage = status === "needed";
  const toggleYN = (k, v) => actions.toggleYN(job.id, k, v);
  const zohoDefault = (flag) => (storage ? (flag ? "yes" : "no") : "");

  return (
    <>
      <SectionLabel icon="clipboard">Storage</SectionLabel>
      <div className="field-row">
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Storage status</label>
          <div className="toolbar-row" style={{ margin: 0 }}>
            <button
              type="button"
              className="btn small"
              onClick={() => actions.setJobField(job.id, "storageStatus", "needed")}
              aria-pressed={status === "needed"}
              style={
                status === "needed"
                  ? {
                      background: "var(--warning)",
                      color: "#fff",
                      borderColor: "var(--warning)",
                    }
                  : undefined
              }
            >
              <Icon name="check" />
              Storage Needed
            </button>
            <button
              type="button"
              className="btn small"
              onClick={() => actions.setJobField(job.id, "storageStatus", "not-required")}
              aria-pressed={status === "not-required"}
              style={
                status === "not-required"
                  ? {
                      background: "var(--success)",
                      color: "#fff",
                      borderColor: "var(--success)",
                    }
                  : undefined
              }
            >
              <Icon name="check" />
              Not Required
            </button>
            {fromZoho ? <span className="pm-hint">From Zoho</span> : null}
          </div>
        </div>
      </div>
      {showStorage ? (
        <>
          <div className="field-row">
            <ReadField label="Storage rate (Zoho)">
              <ZohoMoney value={storage && storage.monthlyFee} />
            </ReadField>
            <ReadField label="Handling fee (Zoho)">
              <ZohoMoney value={storage && storage.handlingFee} />
            </ReadField>
          </div>
          <div className="field-row">
            <ReadField label="Storage first charge (Zoho)">
              <ZohoMoney value={storage && storage.firstCharge} />
            </ReadField>
            <ReadField label="Out of storage date (Zoho)">
              {storage && storage.outOfStorageDate ? fmtDate(storage.outOfStorageDate) : "—"}
            </ReadField>
          </div>
          {storage && storage.facility ? (
            <ReadField label="Storage facility (Zoho)">{storage.facility}</ReadField>
          ) : null}
          <div className="field-row">
            <div className="field">
              <label>Storage start date</label>
              <input
                type="date"
                defaultValue={(job.s2Notes && job.s2Notes.storageStartDate) || ""}
                onBlur={(e) => set("storageStartDate", e.target.value)}
              />
            </div>
            <div className="field">
              <label>LTS out of storage date</label>
              <input
                type="date"
                defaultValue={(job.s2Notes && job.s2Notes.ltsOutDate) || ""}
                onBlur={(e) => set("ltsOutDate", e.target.value)}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Storage rate</label>
              <input
                type="text"
                placeholder="e.g. $225 / month"
                defaultValue={(job.s2Notes && job.s2Notes.storageMonthlyRate) || ""}
                onBlur={(e) => set("storageMonthlyRate", e.target.value)}
              />
            </div>
            <div className="field">
              <label>First storage payment</label>
              <input
                type="text"
                placeholder="e.g. $225 (first month, prorated)"
                defaultValue={(job.s2Notes && job.s2Notes.firstStorageCharge) || ""}
                onBlur={(e) => set("firstStorageCharge", e.target.value)}
              />
            </div>
          </div>
          <div className="liab-card">
            <div className="liab-row">
              <div>LTS included</div>
              <SegYN
                job={job}
                fieldKey="sitIncluded"
                label="LTS included"
                fallback={zohoDefault(storage && storage.lts)}
                onToggleYN={toggleYN}
              />
            </div>
            <div className="liab-row">
              <div>Storage at destination</div>
              <SegYN
                job={job}
                fieldKey="storageDelivery"
                label="Storage at destination"
                fallback={zohoDefault(storage && storage.storeAtDestination)}
                onToggleYN={toggleYN}
              />
            </div>
          </div>
          <div className="field">
            <label>Storage Notes</label>
            <NoteTextarea job={job} fieldKey="storageNotes" onChange={set} />
          </div>
        </>
      ) : null}
    </>
  );
};

const RiskBlock = ({ job, risks, noteKeys, set, actions }) => (
  <>
    <SectionLabel icon="alert">Risks identified</SectionLabel>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {risks.map((r) => {
        const checked = job.riskFlags.includes(r);
        const noteKey = noteKeys[r];
        return (
          <div
            className="field"
            key={r}
            style={
              checked
                ? { borderColor: "var(--danger)", background: "var(--danger-light)" }
                : undefined
            }
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: "12.5px",
                fontWeight: 700,
                cursor: "pointer",
                marginBottom: checked ? 8 : undefined,
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => actions.toggleRisk(job.id, r, e.target.checked)}
              />{" "}
              {r}
            </label>
            {checked ? (
              <NoteTextarea
                job={job}
                fieldKey={noteKey}
                hint="Write details about this risk..."
                onChange={set}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  </>
);

const CallbackBlock = ({ job, actions }) => {
  const [date, setDate] = useState(job.callbackDate || "");
  const [time, setTime] = useState(job.callbackTime || "");
  return (
    <>
      <SectionLabel icon="phone">Callback</SectionLabel>
      <div className="field-row">
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Call back requested?</label>
          <select
            value={job.callBackRequested ? "yes" : "no"}
            onChange={(e) => actions.setCallbackRequested(job.id, e.target.value === "yes")}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>
      </div>
      {job.callBackRequested ? (
        <>
          <div className="field-row">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <input
              type="text"
              placeholder="HH:MM"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>
          <div className="toolbar-row">
            <SaveButton
              small={false}
              idleIcon="bell"
              label="Save callback time"
              savedLabel="Callback saved"
              disabled={!date}
              onSave={() => {
                if (!date) return false;
                actions.saveCallback(job.id, date, time);
              }}
            />
          </div>
        </>
      ) : null}
    </>
  );
};

const RemindersInline = ({ job, actions, wrapped }) => {
  const [text, setText] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const add = () => {
    if (!text.trim()) return false;
    actions.addJobReminder(job.id, text.trim(), date || todayStr(), time);
    setText("");
    setDate("");
    setTime("");
  };
  const form = (
    <>
      <div className="field-row" style={wrapped ? { marginTop: 8 } : undefined}>
        <input
          type="text"
          placeholder="Reminder text"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="reminder-when">
          <input
            type="date"
            aria-label="Reminder date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <input
            type="time"
            aria-label="Reminder time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>
      </div>
      <div className="toolbar-row">
        <SaveButton
          small={false}
          idleIcon="bell"
          label="Add reminder"
          savedLabel="Reminder saved"
          disabled={!text.trim()}
          onSave={add}
        />
      </div>
    </>
  );

  if (wrapped) {
    return (
      <>
        <SectionLabel icon="bell">Reminders</SectionLabel>
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-head">
            <Icon name="bell" />
            Upcoming Reminders
          </div>
          <div className="panel-body">
            {job.reminders.length ? (
              job.reminders.map((r, i) => (
                <div className="reminder-item" key={i}>
                  {r.text}
                  <div className="meta">{fmtReminderWhen(r)}</div>
                </div>
              ))
            ) : (
              <div className="panel-empty">No reminders set.</div>
            )}
          </div>
          <div className="panel-body" style={{ borderTop: "1px solid var(--border)" }}>
            {form}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SectionLabel icon="bell">Reminders</SectionLabel>
      {job.reminders.length ? (
        job.reminders.map((r, i) => (
          <div className="reminder-item" key={i}>
            {r.text}
            <div className="meta">{fmtReminderWhen(r)}</div>
          </div>
        ))
      ) : (
        <div className="panel-empty">No reminders set.</div>
      )}
      {form}
    </>
  );
};

export const TabConfirmationCall = ({ job, isLD, actions }) => {
  const set = makeS2Setter(job, actions.setS2Note);
  const [teamNote, setTeamNote] = useState(job.teamNoteDraft || "");
  const shuttle = shuttleOf(job);
  const shuttleRate = (job.s2Notes && job.s2Notes.shuttleRate) || (shuttle.zohoRate != null ? money(shuttle.zohoRate) : "");
  const bolSigned = !!(job.bolPickupSignedAt || job.bolDeliverySignedAt);

  return (
    <>
      <CallbackBlock job={job} actions={actions} />

      <AiCallBlock job={job} type="pre_move" />

      {isLD ? (
        <>
          <SectionLabel icon="activity">Pick Up</SectionLabel>
          <DestCard
            icon="home"
            label="Pickup Address"
            address={job.moveFrom}
            stats={[
              ["Move date", fmtDate(job.moveDate)],
              ["Packing date", fmtDate(job.packingDate)],
              ["Pickup window", (job.s2Notes && job.s2Notes.pickupWindow) || "—"],
            ]}
          />

          <SectionLabel icon="activity">Delivery</SectionLabel>
          <DestCard
            icon="truck"
            label="Delivery Address"
            address={job.moveTo}
            stats={[
              ["Move date", fmtDate(job.moveDate)],
              ["First ready date", fmtDate(job.deliveryWindowStart)],
              [
                "Delivery window",
                `${fmtDate(job.deliveryWindowStart)} – ${fmtDate(job.deliveryWindowEnd)}`,
              ],
            ]}
          />

          <SectionLabel icon="truck">Shuttle Service</SectionLabel>
          <div className="field-row">
            <div className="field">
              <label>Shuttle Included</label>
              <div className="val">
                <ZohoBadgeVal included={shuttle.included} />
              </div>
            </div>
            <div className="field">
              <label>{shuttle.zohoRate != null ? "Shuttle rate (Zoho)" : "Shuttle rate"}</label>
              <input
                type="text"
                aria-label="Shuttle rate"
                placeholder="$ charge"
                defaultValue={shuttleRate}
                onBlur={(e) => set("shuttleRate", e.target.value)}
              />
            </div>
          </div>
        </>
      ) : null}

      <LogisticsBlock job={job} set={set} />
      <StorageBlock job={job} set={set} actions={actions} />

      <SectionLabel icon="clipboard">Admin</SectionLabel>
      <div className="field-row">
        <S2FieldYN
          job={job}
          fieldKey="confCoiPickup"
          label="COI at Pickup — Done / Required"
          onChange={set}
          onToggleYN={(k, v) => actions.toggleYN(job.id, k, v)}
        />
        <S2FieldYN
          job={job}
          fieldKey="confCoiDelivery"
          label="COI at Delivery — Done / Required"
          onChange={set}
          onToggleYN={(k, v) => actions.toggleYN(job.id, k, v)}
        />
      </div>
      <div className="field-row">
        <div className="field">
          <label>Contract signed (Zoho)</label>
          <div className="val">
            <YesNoBadge value={!!job.contractSigned} />
          </div>
        </div>
        <div className="field">
          <label>BOL signed</label>
          <div className="val">
            <YesNoBadge value={bolSigned} />
          </div>
        </div>
      </div>

      <SectionLabel icon="check" style={{ marginTop: 14 }}>
        Payments
      </SectionLabel>
      <PaymentMethodField job={job} actions={actions} />
      {isHourlyJob(job) ? (
        <HourlyPricing job={job} />
      ) : (
        <ReadField label="Open balance">
          {job.openBalance != null && job.openBalance !== "" ? money(job.openBalance) : "—"}
        </ReadField>
      )}
      {isLD ? (
        <div className="field-row">
          <div className="field">
            <label>Open Balance Before Pickup</label>
            <input
              type="text"
              placeholder="$ amount"
              defaultValue={(job.s2Notes && job.s2Notes.openBalanceBeforePickup) || ""}
              onBlur={(e) => set("openBalanceBeforePickup", e.target.value)}
            />
          </div>
          <ReadField label="Balance Due Before Delivery">
            ${Math.round((job.price || 0) / 2).toLocaleString(LOCALE)}
          </ReadField>
        </div>
      ) : null}
      <S2Field job={job} fieldKey="confPaymentNotes" label="Payment notes" onChange={set} />
      <SectionLabel icon="alert">Liability</SectionLabel>
      <LiabilityCard job={job} set={set} actions={actions} />
      <S2Field job={job} fieldKey="confCrewNotes" label="Any notes for the crew?" onChange={set} />

      <RiskBlock
        job={job}
        risks={isLD ? LD_RISKS : LOCAL_RISKS}
        noteKeys={isLD ? LD_RISK_NOTE_KEYS : RISK_NOTE_KEYS}
        set={set}
        actions={actions}
      />

      {isLD ? (
        <>
          <SectionLabel icon="reply">Note to Sales / Operations</SectionLabel>
          <select defaultValue="sales">
            <option value="sales">Note to Sales Person</option>
            <option value="ops">Note to Operations</option>
          </select>
          <textarea
            rows={4}
            style={{ marginTop: 6 }}
            placeholder="Write your note — phone transcript can be attached"
            value={teamNote}
            onChange={(e) => setTeamNote(e.target.value)}
          />
          <div className="toolbar-row">
            <button
              className="btn"
              onClick={() => {
                if (!teamNote.trim()) return;
                actions.sendTeamNote(job.id, "sales", teamNote.trim());
                setTeamNote("");
              }}
            >
              <Icon name="reply" />
              Sales
            </button>
            <button
              className="btn"
              onClick={() => {
                if (!teamNote.trim()) return;
                actions.sendTeamNote(job.id, "ops", teamNote.trim());
                setTeamNote("");
              }}
            >
              <Icon name="reply" />
              Operations
            </button>
          </div>
        </>
      ) : null}

      <SectionLabel icon="phone">Call log</SectionLabel>
      {isLD ? (
        <div className="panel" style={{ marginBottom: 14 }}>
          <div className="panel-head">
            <Icon name="phone" />
            Recent Calls
          </div>
          <div className="panel-body">
            {job.callLog.length ? (
              job.callLog.map((c, i) => (
                <div className="call-item" key={i}>
                  {c.summary}
                  <div className="meta">{fmtDateTime(c.date)}</div>
                </div>
              ))
            ) : (
              <div className="panel-empty">No calls logged.</div>
            )}
          </div>
        </div>
      ) : (
        <>
          {job.callLog.length ? (
            job.callLog.map((c, i) => (
              <div className="call-item" key={i}>
                {c.summary}
                <div className="meta">{fmtDateTime(c.date)}</div>
              </div>
            ))
          ) : (
            <div className="panel-empty">No calls logged.</div>
          )}
          <div className="toolbar-row">
            <button className="btn" onClick={() => actions.logCall(job.id)}>
              <Icon name="phone" />
              Log a call (just completed)
            </button>
          </div>
        </>
      )}

      <RemindersInline job={job} actions={actions} wrapped={isLD} />

    </>
  );
};
